"use client";

import React from "react";
import { Avatar, AvatarFallback, Button, DropdownMenu, DropdownMenuContent, DropdownMenuSeparator, DropdownMenuTrigger, Spinner } from "@akashnetwork/ui/components";
import { ChartLine, Files, KeyRound, LogOut, Settings, Star, User } from "lucide-react";
import { useRouter } from "next/navigation";
import { useServices } from "@src/context/ServicesProvider";
import { useCustomUser } from "@src/hooks/useCustomUser";
import { CustomDropdownLinkItem } from "../shared/CustomDropdownLinkItem";
export const DEPENDENCIES = {
  useCustomUser,
  useRouter
};
interface Props {
  dependencies?: typeof DEPENDENCIES;
  /** During onboarding, reduce the signed-in menu to the Logout action only. */
  minimal?: boolean;
}
export function AccountMenu({
  dependencies: d = DEPENDENCIES,
  minimal = false
}: Props = {}) {
  const {
    user,
    isLoading
  } = d.useCustomUser();
  const username = user?.username;
  const router = d.useRouter();
  const {
    authService,
    urlService
  } = useServices();
  return <React.Fragment>
      <div className="flex items-center text-center">
        {isLoading ? <div className="pl-2 pr-2">
            <Spinner size="small" />
          </div> : <div className="pl-2 pr-2">
            <DropdownMenu modal={false}>
              <DropdownMenuTrigger asChild>
                <Button size="icon" variant="outline" className="h-9 w-9 cursor-pointer bg-accent" aria-label="Account menu">
                  <Avatar className="h-9 w-9">
                    <AvatarFallback className="bg-transparent">{username ? username[0].toUpperCase() : <User className="h-5 w-5" />}</AvatarFallback>
                  </Avatar>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-[160px]">
                <div className="flex w-full items-center justify-center">
                  {!isLoading && user ? <div className="w-full">
                      {!minimal && <>
                          {username && <CustomDropdownLinkItem onClick={() => router.push(urlService.userProfile(username))} icon={<Avatar className="h-4 w-4">
                                  <AvatarFallback className="text-xs">{username ? username[0].toUpperCase() : <User className="h-3.5 w-3.5" />}</AvatarFallback>
                                </Avatar>}>
                              {username}
                            </CustomDropdownLinkItem>}
                          <DropdownMenuSeparator />
                          <CustomDropdownLinkItem onClick={() => router.push(urlService.userSettings())} icon={<Settings className="h-4 w-4" />}>
                            Profile Settings
                          </CustomDropdownLinkItem>
                          <CustomDropdownLinkItem onClick={() => router.push(urlService.userApiKeys())} icon={<KeyRound className="h-4 w-4" />}>
                            API Keys
                          </CustomDropdownLinkItem>
                          {username && <CustomDropdownLinkItem onClick={() => router.push(urlService.userProfile(username))} icon={<Files className="h-4 w-4" />}>
                              Templates
                            </CustomDropdownLinkItem>}
                          <CustomDropdownLinkItem onClick={() => router.push(urlService.userFavorites())} icon={<Star className="h-4 w-4" />}>
                            Favorites
                          </CustomDropdownLinkItem>
                          {user?.userId && <CustomDropdownLinkItem onClick={() => router.push(urlService.billing())} icon={<ChartLine className="h-4 w-4" />}>
                              Billing & Usage
                            </CustomDropdownLinkItem>}
                          <DropdownMenuSeparator />
                        </>}
                      <CustomDropdownLinkItem onClick={() => authService.logout()} icon={<LogOut className="h-4 w-4" />}>
                        Logout
                      </CustomDropdownLinkItem>
                    </div> : <div className="w-full space-y-1">
                      <CustomDropdownLinkItem className="justify-center bg-primary p-2 text-primary-foreground hover:bg-primary/80 hover:text-primary-foreground focus:bg-primary/80 focus:text-primary-foreground" onClick={() => router.push(urlService.newSignup())}>
                        Sign up
                      </CustomDropdownLinkItem>
                      <CustomDropdownLinkItem onClick={() => router.push(urlService.newLogin())} className="justify-center p-2">
                        Sign in
                      </CustomDropdownLinkItem>
                    </div>}
                </div>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>}
      </div>
    </React.Fragment>;
}